import { ArrowDown01Icon, ReloadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import type { Promo } from '@sunrise-ui/api/events';
import {
  Colors,
  IconButton,
  Loader,
  TextBold,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useOrderCalculations } from '../hooks/useOrderCalculations';

type Props = {
  ticketQuantities: Map<string, number>;
  appliedPromo?: Promo;
  isExpandable?: boolean;
  initialIsExpanded?: boolean;
  onClearDiscount?: () => void;
};

export function OrderSummary({
  ticketQuantities,
  appliedPromo,
  isExpandable,
  onClearDiscount,
  initialIsExpanded,
}: Props) {
  const styles = useDynamicStyles(createStyles, { isExpandable });
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);

  // Check if any tickets are selected
  const hasTickets = Array.from(ticketQuantities.values()).some(
    (qty) => qty > 0,
  );

  // Get order calculations (returns API data if available, otherwise local)
  const { data, isError, refetch, isCalculating } = useOrderCalculations({
    ticketQuantities,
    appliedPromo,
  });

  // If we have no data at all yet, show a basic loader
  if (!data && (!isExpandable || isExpanded)) {
    return (
      <View style={styles.orderSummaryContainer}>
        <View style={styles.headerRow}>
          <TextBold style={styles.orderSummaryTitle}>Order Summary</TextBold>
        </View>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.orderSummaryContainer}>
        <View style={styles.headerRow}>
          <TextBold style={styles.orderSummaryTitle}>Order Summary</TextBold>
        </View>
        <View style={styles.errorContainer}>
          <TextReg style={styles.errorText}>
            Failed to calculate total. Please try again.
          </TextReg>
          <IconButton onPress={refetch} icon={ReloadIcon} />
        </View>
      </View>
    );
  }

  const headerContent = (
    <View style={styles.headerRow}>
      {isExpandable && (
        <View
          style={[
            styles.chevronContainer,
            isExpanded && styles.chevronExpanded,
          ]}
        >
          <HugeiconsIcon icon={ArrowDown01Icon} size={20} color={Colors.text} />
        </View>
      )}
      <TextBold style={styles.orderSummaryTitle}>Order Summary</TextBold>
      {isExpandable && !isExpanded && (
        <>
          {!data || isCalculating ? (
            <View style={styles.skeleton} />
          ) : (
            <TextBold style={styles.collapsedSubtotal}>
              ${data?.total?.toFixed(2) ?? '0.00'}
            </TextBold>
          )}
        </>
      )}
    </View>
  );

  return (
    <View style={styles.orderSummaryContainer}>
      {isExpandable ? (
        <Pressable onPress={() => setIsExpanded(!isExpanded)}>
          {headerContent}
        </Pressable>
      ) : (
        headerContent
      )}

      {(!isExpandable || isExpanded) && (
        <>
          {/* Render line items */}
          {data?.line_items?.map((item) => (
            <View key={item.ticket_type_id} style={styles.orderSummaryRow}>
              <TextReg style={styles.orderSummary}>
                {item.ticket_type_name} ({item.quantity}{' '}
                {item.quantity === 1 ? 'ticket' : 'tickets'})
              </TextReg>
              <TextReg style={styles.orderSummary}>
                ${item.line_total.toFixed(2)}
              </TextReg>
            </View>
          ))}

          {/* Show discount if applied */}
          {hasTickets && data?.promo_code_applied && (
            <View style={styles.orderSummaryRow}>
              <View style={styles.discountLabelRow}>
                <TextReg style={styles.discountText}>
                  Discount ({data.promo_code_applied})
                </TextReg>
                {onClearDiscount && (
                  <Pressable onPress={onClearDiscount}>
                    <TextReg style={styles.clearButton}>clear</TextReg>
                  </Pressable>
                )}
              </View>
              {isCalculating ? (
                <View style={styles.skeleton} />
              ) : (
                <TextReg style={styles.discountText}>
                  -${data?.discount_amount?.toFixed(2)}
                </TextReg>
              )}
            </View>
          )}

          {/* Show tax row with loading skeleton or actual value */}
          {hasTickets && (
            <View style={styles.orderSummaryRow}>
              <TextReg style={styles.orderSummary}>
                {isCalculating
                  ? 'Taxes'
                  : `Taxes (${((data?.tax_rate ?? 0) * 100)?.toFixed(2)}%)`}
              </TextReg>
              {isCalculating ? (
                <View style={styles.skeleton} />
              ) : (
                <TextReg style={styles.orderSummary}>
                  ${data?.tax_amount?.toFixed(2)}
                </TextReg>
              )}
            </View>
          )}

          <View style={styles.orderSummaryDivider} />

          <View style={styles.orderSummaryTotalRow}>
            <TextBold style={styles.orderSummaryTotal}>Total</TextBold>
            {isCalculating ? (
              <View style={styles.skeleton} />
            ) : (
              <TextBold style={styles.orderSummaryTotal}>
                ${data?.total?.toFixed(2) ?? '0.00'}
              </TextBold>
            )}
          </View>
        </>
      )}
    </View>
  );
}

const createStyles = ({ isExpandable }: WithResponsive<Props>) =>
  StyleSheet.create({
    orderSummaryContainer: {
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
      padding: isExpandable ? 12 : 16,
      gap: 12,
      width: '100%',
      maxWidth: 400,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isExpandable ? 8 : 0,
    },
    chevronContainer: {
      transform: [{ rotate: '-90deg' }],
    },
    chevronExpanded: {
      transform: [{ rotate: '0deg' }],
    },
    orderSummaryTitle: {
      fontSize: isExpandable ? 16 : 20,
      flex: 1,
    },
    collapsedSubtotal: {
      fontSize: 16,
      justifyContent: 'center',
      display: 'flex',
    },
    orderSummaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    orderSummary: {
      fontSize: isExpandable ? 12 : 14,
    },
    orderSummaryDivider: {
      height: 1,
      backgroundColor: Colors.opacity20,
    },
    orderSummaryTotalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    orderSummaryTotal: {
      fontSize: isExpandable ? 16 : 20,
    },
    discountAmount: {
      fontSize: isExpandable ? 12 : 14,
      color: Colors.orange0,
    },
    discountText: {
      fontSize: isExpandable ? 12 : 14,
      color: Colors.green0,
    },
    discountLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    clearButton: {
      fontSize: 12,
      color: Colors.opacity50,
    },
    skeleton: {
      width: 50,
      height: 14,
      backgroundColor: Colors.opacity20,
      borderRadius: 4,
    },
    loaderContainer: {
      paddingVertical: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorContainer: {
      paddingVertical: 12,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
    },
    errorText: {
      fontSize: 12,
      color: Colors.error,
      textAlign: 'center',
    },
    retryButton: {
      // paddingHorizontal: 24,
    },
  });
